'use client';

import { keepPreviousData, queryOptions, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';
import { useId, useRef, useState, type KeyboardEvent } from 'react';

import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchPrinterManufacturers } from '@eliadmin/core/src/utils/nt/printer-catalog-api';
import { Icon } from '@eliadmin/core/src/components/nt/ui/icon';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useDebounce } from '@eliadmin/core/src/hooks/use-debounce';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { printerHref } from '@eliadmin/core/src/utils/nt/printer-finder';
import { cn } from '@eliadmin/core/src/utils/style-utils';

/** Figma „div#hladat“: white field 56 px (icon, input, button M). */
export const SEARCH_FIELD =
  'flex items-center gap-3 rounded-2xl bg-white py-1 pl-5 pr-1 transition-shadow sm:gap-6';

const MIN_QUERY = 2;
const SUGGESTIONS = 8;
const DEBOUNCE_MS = 300;

type PrinterModel = { id: number | string; name: string; slug: string };

/**
 * Printer models = verified child manufacturers under the selected printer brands (also the
 * „Tlačiarne“ group of the header search).
 */
export async function fetchPrinterModels(
  locale: string,
  brandIds: (number | string)[],
  search: string,
  signal?: AbortSignal,
  results = SUGGESTIONS,
): Promise<PrinterModel[]> {
  if (!brandIds.length) return [];
  return (await fetchPrinterModelPage(locale, brandIds, search, signal, results)).data;
}

export async function fetchPrinterModelPage(
  locale: string,
  brandIds: (number | string)[],
  search: string,
  signal?: AbortSignal,
  results = 18,
  page = 1,
) {
  const { data, meta } = await fetchPrinterManufacturers(
    locale,
    { parents: brandIds, search, results, page },
    signal,
  );
  return { data, meta };
}

const comparable = (text: string) => text.toLocaleLowerCase().replace(/\s+/g, ' ').trim();

/**
 * „Podľa modelu tlačiarne“: combobox with printer-model suggestions (WAI-ARIA combobox + listbox).
 * Submitting opens the chosen or the only/exactly matching model category; several matches keep
 * the list open; no match goes to the product search (cartridge codes).
 */
export function PrinterModelSearch({
  labelledBy,
  placeholder,
  submit,
  noResults,
  brandIds,
  tonerScope,
  modelOnly = false,
}: {
  labelledBy: string;
  placeholder: string;
  submit: string;
  noResults: string;
  brandIds: (number | string)[];
  tonerScope?: string;
  modelOnly?: boolean;
}) {
  const locale = useLocaleStore(state => state.currentLocale);
  const router = useRouter();
  const queryClient = useQueryClient();
  const listId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const search = useDebounce(query.trim(), DEBOUNCE_MS);

  const models = (term: string) =>
    queryOptions({
      queryKey: [CacheKey.CATEGORIES, 'printer-manufacturer-models', locale, brandIds, term],
      queryFn: ({ signal }) => fetchPrinterModels(locale, brandIds, term, signal),
    });
  const { data, isFetching, isError } = useQuery({
    ...models(search),
    enabled: search.length >= MIN_QUERY,
    placeholderData: keepPreviousData,
  });

  const suggestions = search.length >= MIN_QUERY ? (data ?? []) : [];
  // „No results“ only for a finished search of the current text, not for a failed request.
  const empty =
    !suggestions.length &&
    search.length >= MIN_QUERY &&
    search === query.trim() &&
    !isFetching &&
    !isError;
  const expanded = open && (suggestions.length > 0 || empty);

  function go(model: PrinterModel) {
    setOpen(false);
    router.push(printerHref(model.slug, tonerScope));
  }

  async function onSubmit() {
    const term = query.trim();
    if (!term) {
      inputRef.current?.focus();
      return;
    }
    if (expanded && suggestions[active]) {
      go(suggestions[active]);
      return;
    }
    const found =
      term.length >= MIN_QUERY ? await queryClient.fetchQuery(models(term)).catch(() => []) : [];
    const exact = found.find(model => comparable(model.name) === comparable(term));
    if (exact || found.length === 1) {
      go(exact ?? found[0]);
    } else if (found.length) {
      setOpen(true);
      setActive(0);
      inputRef.current?.focus();
    } else if (modelOnly) {
      setOpen(true);
      inputRef.current?.focus();
    } else {
      router.push(ROUTES.SEARCH(term));
    }
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setOpen(true);
      if (!suggestions.length) return;
      // -1 = the input itself; the arrows cycle input → options → input.
      const positions = suggestions.length + 1;
      const step = event.key === 'ArrowDown' ? 1 : -1;
      setActive(index => ((index + 1 + step + positions) % positions) - 1);
    } else if (event.key === 'Escape' && expanded) {
      event.preventDefault();
      setOpen(false);
      setActive(-1);
    }
  }

  return (
    <div className="relative">
      <form
        role="search"
        aria-labelledby={labelledBy}
        onSubmit={event => {
          event.preventDefault();
          void onSubmit();
        }}
        className={cn(
          SEARCH_FIELD,
          'has-[input:focus-visible]:shadow-focus',
          modelOnly && 'max-sm:flex-wrap max-sm:p-3',
        )}
      >
        <span className="flex min-w-0 flex-1 items-center gap-2">
          <Icon name="magnifying-glass" className="text-[#8F978E]" />
          <input
            ref={inputRef}
            type="text"
            role="combobox"
            aria-labelledby={labelledBy}
            aria-autocomplete="list"
            aria-expanded={expanded}
            aria-controls={listId}
            aria-activedescendant={expanded && active >= 0 ? `${listId}-${active}` : undefined}
            autoComplete="off"
            enterKeyHint="search"
            placeholder={placeholder}
            value={query}
            onChange={event => {
              setQuery(event.target.value);
              setOpen(true);
              setActive(-1);
            }}
            onFocus={() => setOpen(true)}
            onBlur={() => setOpen(false)}
            onKeyDown={onKeyDown}
            className="nt-body-l h-12 min-w-0 flex-1 text-ellipsis bg-transparent text-[#0D110D] outline-none placeholder:text-[#8F978E]"
          />
        </span>
        <Button type="submit" className={modelOnly ? 'max-sm:w-full' : undefined}>
          {submit}
        </Button>
      </form>
      <div
        hidden={!expanded}
        className="absolute inset-x-0 top-full z-20 mt-2 rounded-2xl bg-white p-2 shadow-float"
      >
        <ul id={listId} role="listbox" aria-labelledby={labelledBy}>
          {suggestions.map((model, index) => (
            <li
              key={model.id}
              id={`${listId}-${index}`}
              role="option"
              aria-selected={index === active}
              onMouseDown={event => event.preventDefault()}
              onMouseEnter={() => setActive(index)}
              onClick={() => go(model)}
              className="nt-body-m cursor-pointer rounded-xl px-3 py-2.5 text-[#0D110D] aria-selected:bg-[#F5F6F3]"
            >
              {model.name}
            </li>
          ))}
        </ul>
        {suggestions.length ? null : (
          <p className="nt-body-m px-3 py-2.5 text-[#5D655D]">{noResults}</p>
        )}
      </div>
      {/* Outside the hidden panel, so the change is announced. */}
      <p role="status" className="sr-only">
        {expanded && !suggestions.length ? noResults : ''}
      </p>
    </div>
  );
}
